import React, { useState, useEffect, useRef } from 'react';
import './ChatGPTInput.css';
import LoadingSpinner from './Common/LoadingSpinner';
import HistoryScreen from './HistoryScreen'; // Import the HistoryScreen
import { logEvent } from '../utils/Logger';
import historyIcon from '../images/history_icon.svg';
interface ChatGPTInputProps {
  lastApiCallResult: {
    suggestions?: Array<{ title: string; idea: string }>;
    logicalFlaws?: Array<{ problem: string; improvement: string }>;
    frameValidation?: {
      mainFrame?: string;
      isAligned?: boolean;
      explanation?: string;
      ideas?: string[];
    };
  };
  newApiCallMade: boolean;
  isLoading: boolean;
}

const ChatGPTInput: React.FC<ChatGPTInputProps> = ({ lastApiCallResult, newApiCallMade, isLoading }) => {
  const [displayedSuggestions, setDisplayedSuggestions] = useState<Array<{ title: string; idea: string }>>([]);
  const [displayedLogicalFlaws, setDisplayedLogicalFlaws] = useState<Array<{ problem: string; improvement: string }>>([]);
  const [displayedFrameValidation, setDisplayedFrameValidation] = useState<{
    mainFrame?: string;
    isAligned?: boolean;
    explanation?: string;
    ideas?: string[];
  }>({});
  const [chatGPTSectionTitle, setChatGPTSectionTitle] = useState<string>('');
  const [isHistoryOpen, setHistoryOpen] = useState(false);
  const toggleHistory = () => {
    const eventName = isHistoryOpen ? 'close_history' : 'open_history';
    logEvent(eventName, '', window.location.href);
    setHistoryOpen(!isHistoryOpen)
  }
  const responseContainerRef = useRef<HTMLDivElement>(null);
  const [historyEntries, setHistoryEntries] = useState<{ title: string; content: any; timestamp: string }[]>([]); // Update state to hold objects with timestamp

  const handleSelectEntry = (entry: { title: string; content: any }) => {
    // Logic to update displayed content based on the selected history entry
    const payload = {
      title: entry.title,
      content: entry.content,
    }
    logEvent('select_history_entry', JSON.stringify(payload), window.location.href);
    if (entry.title === "Suggest Discussion Points") {
      setDisplayedSuggestions(entry.content.suggestions);
      setDisplayedLogicalFlaws([]);
      setDisplayedFrameValidation({});
    } else if (entry.title === "Feedback on Comment") {
      setDisplayedLogicalFlaws(entry.content.logicalFlaws);
      setDisplayedSuggestions([]);
      setDisplayedFrameValidation({});
    }
    setHistoryOpen(false); // Close history after selection
  };

  useEffect(() => {
    if (newApiCallMade) {
      setChatGPTSectionTitle("")
      setDisplayedSuggestions(lastApiCallResult.suggestions || []);
      setDisplayedLogicalFlaws(lastApiCallResult.logicalFlaws || []);
      setDisplayedFrameValidation(lastApiCallResult.frameValidation || {});
      const entryTitle = lastApiCallResult.suggestions?.length ? "Suggest Discussion Points" : "Feedback on Comment";
      const entryContent = {
        suggestions: lastApiCallResult.suggestions || [],
        logicalFlaws: lastApiCallResult.logicalFlaws || [],
        frameValidation: lastApiCallResult.frameValidation || {},
      };
      const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }); // Format to HH:MM:SS
      setHistoryEntries(prevEntries => [...prevEntries, { title: entryTitle, content: entryContent, timestamp }]); // Save title, content, and timestamp
    }
  }, [newApiCallMade, lastApiCallResult]);

  useEffect(() => {
      if (displayedSuggestions.length > 0) {
        setChatGPTSectionTitle("Suggest Discussion Points");
      } else if (displayedLogicalFlaws.length > 0) {
        setChatGPTSectionTitle("Feedback on Comment");
      } else if (displayedFrameValidation.mainFrame) {
        setChatGPTSectionTitle("Frame Validation");
      } else {
        setChatGPTSectionTitle("");
      }
    }, [displayedSuggestions, displayedLogicalFlaws, displayedFrameValidation]);

  useEffect(() => {
    if (responseContainerRef.current) {
      responseContainerRef.current.scrollTop = 0;
    }
  }, [displayedSuggestions, displayedLogicalFlaws, displayedFrameValidation]);



  return (
    <div className="chatgpt-input">
      <div className="chatgpt-header">
        <h3>{chatGPTSectionTitle}</h3>
        <button className={`history-button ${isHistoryOpen ? 'active' : ''}`} onClick={toggleHistory}> {/* No width style needed */}
          <img src={historyIcon} alt="History" width="16" height="16" />
          {/* <span>History</span> */}
        </button> 
      </div>
      
      <HistoryScreen 
        isOpen={isHistoryOpen} 
        onClose={toggleHistory} 
        historyEntries={historyEntries} 
        onSelectEntry={handleSelectEntry} 
      /> 
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="chatgpt--response-container" ref={responseContainerRef}>
          {displayedSuggestions.length > 0 && (
            <div className="response suggestions">
              {/* <h3 className="pinned-header">Suggested Moral Arguments</h3> */}
              <div className="suggestion-grid">
                {displayedSuggestions.map((suggestion, index) => (
                  <div key={index} className="generation--box suggestion">
                    <h4 className="generation--title">{suggestion.title}</h4>
                    <p className="generation--content">{suggestion.idea}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {displayedLogicalFlaws.length > 0 && (
            <div className="response logical-flaws">
              {/* <h3 className="pinned-header">Identified Logical Flaws</h3> */}
              <div className="flaw-grid">
                {displayedLogicalFlaws.map((flaw, index) => (
                  <div key={index} className="generation--box flaw">
                    <h4 className="generation--title"><strong>Feedback {index + 1}:</strong> {flaw.problem}</h4>
                    <p className="generation--content">
                      {/* <strong>Improvement:</strong>  */}
                      {flaw.improvement}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {displayedFrameValidation.mainFrame && (
            <div className="response frame-validation">
              {/* <h3 className="pinned-header">Frame Validation</h3> */}
              <div className="frame-validation-content">
                <div className="frame-validation-item">
                  <span className="frame-validation-label">Main Frame:</span>
                  <p className="frame-validation-text">{displayedFrameValidation.mainFrame}</p>
                </div>
                <div className="frame-validation-item">
                  <span className="frame-validation-label">Alignment:</span>
                  <span className={`frame-validation-alignment ${displayedFrameValidation.isAligned ? 'aligned' : 'not-aligned'}`}>
                    {displayedFrameValidation.isAligned ? 'Aligned' : 'Not Aligned'}
                  </span>
                </div>
                <div className="frame-validation-item">
                  <span className="frame-validation-label">Explanation:</span>
                  <p className="frame-validation-text">{displayedFrameValidation.explanation}</p>
                </div>
                {displayedFrameValidation.ideas && displayedFrameValidation.ideas.length > 0 && (
                  <div className="frame-validation-item">
                    <span className="frame-validation-label">Ideas for Improvement:</span>
                    <div className="frame-validation-ideas">
                      {displayedFrameValidation.ideas.map((idea, index) => (
                        <div key={index} className="frame-validation-idea-box">
                          {idea}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatGPTInput;